import type { ReviewEventDto } from "@reviewer73/shared";
import { useEffect, useRef, useState } from "react";
import { api } from "../api/client.js";
import { Button } from "./ui/button.js";
import { formatTime } from "../lib/format.js";

/**
 * The pipeline's live progress. Polled rather than pushed: a review takes minutes
 * and a two-second poll is indistinguishable from a stream at that scale, while
 * needing no long-lived connection through the ingress.
 *
 * The cursor is the `seq` of the last event seen, so a poll transfers only what is
 * new and the list survives a slow request without duplicating rows.
 */

const PHASE_LABEL: Record<string, string> = {
  fetch: "Kontext",
  gitleaks: "Scanner",
  review: "Review",
  architecture: "Architektur",
  post: "Zustellung",
};

/** Every kind except the reasoning stream, which is opt-in — it dwarfs the rest. */
const QUIET_KINDS = ["status", "tool", "log", "metric"];
const POLL_MS = 2000;
/** The backend's page size. A full page means there is more waiting right now. */
const PAGE = 500;

export function ReviewStream({ reviewId, live }: { reviewId: string; live: boolean }) {
  const [showReasoning, setShowReasoning] = useState(false);
  const [events, setEvents] = useState<ReviewEventDto[]>([]);
  const [failed, setFailed] = useState(false);
  const scrollBox = useRef<HTMLDivElement | null>(null);
  const pinnedToBottom = useRef(true);

  useEffect(() => {
    // Changing the filter changes what the cursor means, so the list starts over.
    setEvents([]);
    setFailed(false);
    let cursor = -1;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async (): Promise<void> => {
      try {
        const batch = await api.reviewEvents(reviewId, {
          afterSeq: cursor,
          kinds: showReasoning ? undefined : QUIET_KINDS,
        });
        if (cancelled) return;
        setFailed(false);
        if (batch.length > 0) {
          cursor = batch[batch.length - 1]!.seq;
          setEvents((prev) => [...prev, ...batch]);
        }
        // Catching up on a finished review: keep going until the page is short,
        // then only keep polling while the review is actually still running.
        if (batch.length === PAGE) timer = setTimeout(poll, 0);
        else if (live) timer = setTimeout(poll, POLL_MS);
      } catch {
        // A failed poll is not an error state to recover from, just a tick lost.
        if (cancelled) return;
        setFailed(true);
        if (live) timer = setTimeout(poll, POLL_MS);
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [reviewId, live, showReasoning]);

  // Follows the tail, but stops fighting the user the moment they scroll up.
  useEffect(() => {
    const box = scrollBox.current;
    if (box && pinnedToBottom.current) box.scrollTop = box.scrollHeight;
  }, [events]);

  return (
    <div className="tile p-1">
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span className="text-xs text-muted-foreground">
          {events.length === 0
            ? live
              ? "warte auf das erste Lebenszeichen der Pipeline …"
              : "Für dieses Review wurde kein Verlauf aufgezeichnet."
            : `${events.length} Ereignis(se)${failed ? " · Abruf gerade gestört" : ""}`}
        </span>
        <Button variant="outline" size="sm" onClick={() => setShowReasoning((on) => !on)}>
          {showReasoning ? "Denkprozess ausblenden" : "Denkprozess einblenden"}
        </Button>
      </div>

      {events.length > 0 ? (
        <div
          ref={scrollBox}
          onScroll={(event) => {
            const box = event.currentTarget;
            pinnedToBottom.current =
              box.scrollHeight - box.scrollTop - box.clientHeight < 40;
          }}
          className="max-h-96 overflow-y-auto border-t border-border px-3 py-2 font-mono text-xs"
        >
          {events.map((item) => (
            <div key={item.seq} className="flex gap-2 py-0.5">
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatTime(item.at)}
              </span>
              <span className="w-20 shrink-0 truncate text-muted-foreground">
                {PHASE_LABEL[item.phase] ?? item.phase}
              </span>
              <span
                className={
                  item.level === "error"
                    ? "whitespace-pre-wrap break-words text-destructive"
                    : item.level === "warn"
                      ? "whitespace-pre-wrap break-words text-amber-600 dark:text-amber-500"
                      : item.kind === "reasoning"
                        ? "whitespace-pre-wrap break-words italic text-muted-foreground"
                        : item.kind === "status"
                          ? "whitespace-pre-wrap break-words font-semibold"
                          : "whitespace-pre-wrap break-words"
                }
              >
                {item.message}
              </span>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
